import React, {useState, useCallback, useEffect, useMemo} from 'react';
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {
  faChevronDown,
  faChevronUp,
  faInfoCircle,
  faUserTie,
  faListCheck
} from "@fortawesome/free-solid-svg-icons";
import EmployeePosition from "./EmployeePosition";
import EmployeeLaborCostOverview from "./EmployeeLaborCostOverview";
import {faCirclePlus} from "@fortawesome/free-solid-svg-icons/faCirclePlus";
import {employeePositionDefault} from "../../../../defaults/Items";
import "./EmployeePositionsCard.css";

const RATE_DEFAULTS = {
  rateSource: "",
  rateBillingType: "HOURLY",
  hourlyRate: "",
  netHourlyRate: "",
  monthlyRate: "",
  netMonthlyRate: ""
};

const EmployeePositionsCard = ({state, positionsList, settings}) => {
  const currentItem = state.currentItem;
  const employeePositions = useMemo(() => currentItem?.employeePositions || [], [currentItem?.employeePositions]);
  const setCurrentItem = state.setCurrentItem;
  const setItemChanged = state.setItemChanged;
  const [positionsNote] = useState(false);
  const [isAccordionOpen, setIsAccordionOpen] = useState(true);
  const isAdding = !!state.currentFormState?.formAddingDataMode;
  const selectedDepartmentId = Number(currentItem?.department?.id || 0);
  const filteredPositionsList = useMemo(() => positionsList.filter(position => {
    if (employeePositions.some(empPos => empPos.position?.id === position.id)) return false;
    if (isAdding && selectedDepartmentId > 0 && Number(position?.department?.id || 0) !== selectedDepartmentId) return false;
    return true;
  }), [positionsList, employeePositions, isAdding, selectedDepartmentId]);
  const [showPositionsModal, setShowPositionsModal] = useState(false);
  const studentOrPupil = !!currentItem?.student || !!currentItem?.pupil;
  const costProfile = studentOrPupil && currentItem?.krus
    ? {label: "Konflikt profili", className: "bg-danger"}
    : studentOrPupil
      ? {label: "Uczeń / student", className: "bg-info"}
      : currentItem?.krus
        ? {label: "KRUS", className: "bg-success"}
        : {label: "ZUS", className: "bg-secondary"};

  const [selectedIds, setSelectedIds] = useState([]);

  useEffect(() => {
    setSelectedIds(previous => previous.filter(id => filteredPositionsList.some(position => position.id === id)));
  }, [filteredPositionsList]);

  useEffect(() => {
    if (!isAdding || selectedDepartmentId <= 0) return;
    const validPositionIds = new Set(positionsList
      .filter(position => Number(position?.department?.id || 0) === selectedDepartmentId)
      .map(position => position.id));
    if (employeePositions.every(employeePosition => !employeePosition?.position?.id || validPositionIds.has(employeePosition.position.id))) return;
    setCurrentItem(previous => ({
      ...previous,
      employeePositions: (previous.employeePositions || []).filter(employeePosition =>
        !employeePosition?.position?.id || validPositionIds.has(employeePosition.position.id)
      )
    }));
    setItemChanged(true);
  }, [isAdding, selectedDepartmentId, positionsList, employeePositions, setCurrentItem, setItemChanged]);

  const toggleSelect = (id) => setSelectedIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  const getPositionLabel = (p) => `${p.name || p.title || p.code || `ID ${p.id}`}${p.department?.name ? ` — ${p.department.name}` : ""}`;

  const allVisibleSelected = filteredPositionsList.length > 0 && filteredPositionsList.every(p => selectedIds.includes(p.id));
  const toggleSelectAll = () => setSelectedIds(allVisibleSelected ? [] : filteredPositionsList.map(p => p.id));

  const updatePosition = useCallback((positionId, changes) => {
    setCurrentItem(prev => ({
      ...prev,
      employeePositions: (prev.employeePositions || []).map(item =>
        item.id === positionId ? {...item, ...changes} : item
      )
    }));
    setItemChanged(true);
  }, [setCurrentItem, setItemChanged]);

  const addPosition = () => {
    const existingIds = employeePositions.map((position) => position.id);
    let newId = -1;
    while (existingIds.includes(newId)) newId--;

    const newPosition = {
      ...employeePositionDefault,
      ...RATE_DEFAULTS,
      EID: currentItem.id,
      id: newId
    };
    setCurrentItem({...currentItem, employeePositions: [...employeePositions, newPosition]});
    setItemChanged(true);
  };

  const addPositionsFromList = () => {
    const existingIds = new Set(employeePositions.map(p => p.id));
    let nextId = -1;
    while (existingIds.has(nextId)) nextId--;

    const toAdd = selectedIds
      .map(id => filteredPositionsList.find(p => p.id === id))
      .filter(Boolean)
      .map(pos => {
        const ep = {
          ...employeePositionDefault,
          ...RATE_DEFAULTS,
          EID: currentItem.id,
          id: nextId,
          position: pos
        };
        existingIds.add(nextId);
        nextId--;
        while (existingIds.has(nextId)) nextId--;
        return ep;
      });

    setCurrentItem({...currentItem, employeePositions: [...employeePositions, ...toAdd]});
    setItemChanged(true);
    setSelectedIds([]);
    setShowPositionsModal(false);
  };

  const removePosition = useCallback((positionId) => {
    setCurrentItem(prev => ({
      ...prev,
      employeePositions: (prev.employeePositions || []).filter(item => item.id !== positionId)
    }));
    setItemChanged(true);
  }, [setCurrentItem, setItemChanged]);

  return (
    <>
      <div className="card employee-positions-card">
        <div
          className="card-header d-flex align-items-center justify-content-between"
          onClick={() => setIsAccordionOpen(!isAccordionOpen)}
          style={{cursor: "pointer"}}
        >
          <div className="d-flex align-items-center">
            <FontAwesomeIcon icon={faUserTie} className="me-2 header-icon"/>
            <span>Stanowiska pracownika {!isAccordionOpen ? `(${employeePositions.length})` : ""}</span>
            <span
              className={`badge ${costProfile.className} ms-2`}
              title="Status pracownika wpływający na kalkulację kosztu. Ostateczny profil zależy także od wieku, umowy i podstawy ZUS."
            >
              {costProfile.label}
            </span>
          </div>
          <FontAwesomeIcon icon={isAccordionOpen ? faChevronUp : faChevronDown}/>
        </div>

        <div className={`card-body ${isAccordionOpen ? 'show' : 'offscreen'}`} id="employeePositions">
          <EmployeeLaborCostOverview
            employeeId={currentItem?.id}
            employeePositions={employeePositions}
          />

          <div className="employee-positions-toolbar">
            <div className="employee-positions-actions">
              <button type="button" className="btn-auxiliary btn-primary" onClick={addPosition}>
                <FontAwesomeIcon icon={faCirclePlus}/> Dodaj
              </button>
              <button type="button" className="btn-auxiliary btn-primary" onClick={() => setShowPositionsModal(true)}>
                <FontAwesomeIcon icon={faListCheck}/> Wybierz z listy
              </button>
            </div>
            <span className="small text-muted">
              {isAdding && selectedDepartmentId > 0
                ? "Lista stanowisk jest ograniczona do wybranego działu."
                : "Stawka bazowa jest zapisywana indywidualnie przy pracowniku. Możesz skopiować ją z aktualnej umowy, z definicji stanowiska albo podać ręcznie."}
            </span>
          </div>

          <div className="employee-positions-list">
            {employeePositions.length > 0 ? employeePositions.map((position, index) => (
              <EmployeePosition
                key={position.id ?? index}
                employeePosition={position}
                index={index}
                state={state}
                onChange={(changes) => updatePosition(position.id, changes)}
                positionsList={filteredPositionsList}
                onRemove={() => removePosition(position.id)}
                settings={settings}
              />
            )) : (
              <p className="employee-positions-empty">Brak przydzielonych stanowisk</p>
            )}
          </div>
        </div>
      </div>

      <p id="positionsNote" className={positionsNote ? "err_info" : "offscreen"}>
        <FontAwesomeIcon icon={faInfoCircle}/>&nbsp; Uzupełnij dane stanowiska
      </p>

      {showPositionsModal && (
        <div className="employee-position-select-backdrop">
          <div className="employee-position-select-modal">
            <div className="p-3 border-bottom d-flex justify-content-between align-items-center">
              <h5 className="m-0">Wybierz stanowiska</h5>
              <div className="d-flex align-items-center gap-2">
                <button
                  type="button"
                  className="btn btn-sm btn-outline-secondary"
                  onClick={toggleSelectAll}
                  disabled={filteredPositionsList.length === 0}
                >
                  {allVisibleSelected ? 'Odznacz wszystkie' : 'Zaznacz wszystkie'}
                </button>
                <button type="button" className="btn btn-sm btn-light" onClick={() => setShowPositionsModal(false)}>×</button>
              </div>
            </div>

            <div className="p-3 employee-position-select-list">
              {filteredPositionsList.length === 0 ? (
                <p>Brak dostępnych pozycji</p>
              ) : (
                <ul>
                  {filteredPositionsList.map((p) => (
                    <li key={p.id}>
                      <input
                        type="checkbox"
                        id={`pos-${p.id}`}
                        className="form-check-input me-2"
                        checked={selectedIds.includes(p.id)}
                        onChange={() => toggleSelect(p.id)}
                      />
                      <label htmlFor={`pos-${p.id}`} className="form-check-label">{getPositionLabel(p)}</label>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            <div className="p-3 border-top d-flex justify-content-end gap-2">
              <button type="button" className="btn btn-secondary" onClick={() => setShowPositionsModal(false)}>Anuluj</button>
              <button type="button" className="btn btn-primary" disabled={selectedIds.length === 0} onClick={addPositionsFromList}>Dodaj</button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default EmployeePositionsCard;
